import { BREAKPOINT_VIEWPORTS } from "@/features/page-editor/lib/responsive"
import { captureElementAtViewport } from "./offscreen-capture"
import {
  createAgentRuntimeClient,
  GEMMA_VISUAL_REVIEW_SKILL_ID,
  sendGemmaVisualReviewIfAdvertised,
  skillIdsFromAgentCard,
  type PostA2AResult,
} from "./studio-agent-runtime-a2a"

/**
 * Why a review produced no answer.
 *
 * These were all one bare `null`, so the screen said "this runtime does not
 * offer visual review" even when the runtime advertised it and the capture was
 * what failed — a false sentence, and the same silence this lane exists to
 * remove one layer down.
 */
export type PreviewReviewGiveUpReason =
  | "skill-not-advertised"
  | "surface-not-rendered"
  | "capture-failed"

export interface PreviewReviewGiveUp {
  giveUp: PreviewReviewGiveUpReason
}

export function isPreviewReviewGiveUp(
  value: unknown,
): value is PreviewReviewGiveUp {
  return typeof value === "object" && value !== null && "giveUp" in value
}

const PNG_DATA_URL_PREFIX = "data:image/png;base64,"
const GENERATED_APP_TEST_ID = "generated-app-rendered"

export function pngBytesFromDataUrl(dataUrl: string): string | null {
  if (!dataUrl.startsWith(PNG_DATA_URL_PREFIX)) return null
  const bytes = dataUrl.slice(PNG_DATA_URL_PREFIX.length)
  return bytes.length > 0 ? bytes : null
}

export function studioGemmaScreenshotParams(input: {
  scenarioId: string
  appName: string
  userRequest: string
  pageCount: number
  desktopPngBytes: string
  mobilePngBytes: string
}): Record<string, unknown> {
  return {
    scenarioId: input.scenarioId,
    appName: input.appName,
    userRequest: input.userRequest,
    pageCount: input.pageCount,
    screenshots: [
      {
        label: "desktop",
        pngBytes: input.desktopPngBytes,
        viewport: BREAKPOINT_VIEWPORTS.desktop,
      },
      {
        label: "mobile",
        pngBytes: input.mobilePngBytes,
        viewport: BREAKPOINT_VIEWPORTS.mobile,
      },
    ],
  }
}

export async function reviewPreviewWithAdvertisedGemma(options: {
  origin: string
  applicationId: string
  accessToken: string
  /**
   * The agent card the caller already resolved.
   *
   * `resolveAdvertisedAgentRuntime` fetches this to decide which runtime
   * answers, so re-fetching it here made every reviewed page cost two GETs of
   * the same /.well-known/agent-card.json.
   */
  card: unknown
  scenarioId: string
  appName: string
  userRequest: string
  pageCount: number
  messageId: string
  capture: (element: HTMLElement) => Promise<string>
  queryRoot: ParentNode
  waitForPaint: () => Promise<void>
  captureTimeoutMs?: number
}): Promise<PostA2AResult | PreviewReviewGiveUp> {
  const client = createAgentRuntimeClient({
    origin: options.origin,
    applicationId: options.applicationId,
    accessToken: options.accessToken,
  })
  // The same predicate the sender uses, so a card it would refuse never costs
  // two screenshots first. A `=== null` check let `undefined` straight past.
  if (!skillIdsFromAgentCard(options.card).includes(GEMMA_VISUAL_REVIEW_SKILL_ID)) {
    return { giveUp: "skill-not-advertised" }
  }

  const appElement = options.queryRoot.querySelector(
    `[data-testid="${GENERATED_APP_TEST_ID}"]`,
  )
  if (!(appElement instanceof HTMLElement)) {
    return { giveUp: "surface-not-rendered" }
  }

  // Both shots come from clones in offscreen hosts. The creator's frame is
  // never re-laid-out, which is what they used to watch happen per page.
  let desktopBytes: string | null
  let mobileBytes: string | null
  try {
    desktopBytes = pngBytesFromDataUrl(
      await captureElementAtViewport({
        element: appElement,
        viewport: BREAKPOINT_VIEWPORTS.desktop,
        capture: options.capture,
        waitForPaint: options.waitForPaint,
        timeoutMs: options.captureTimeoutMs,
      }),
    )
    mobileBytes = pngBytesFromDataUrl(
      await captureElementAtViewport({
        element: appElement,
        viewport: BREAKPOINT_VIEWPORTS.mobile,
        capture: options.capture,
        waitForPaint: options.waitForPaint,
        timeoutMs: options.captureTimeoutMs,
      }),
    )
  } catch {
    return { giveUp: "capture-failed" }
  }
  if (desktopBytes === null || mobileBytes === null) {
    return { giveUp: "capture-failed" }
  }

  // The sender re-checks the skill and answers `null` when it is absent. We
  // guarded on the same predicate above, so that `null` can only mean the one
  // thing — name it rather than letting it widen the return type back out.
  const sent = await sendGemmaVisualReviewIfAdvertised({
    card: options.card,
    sendMessage: (message) => client.sendMessage(message),
    messageId: options.messageId,
    params: studioGemmaScreenshotParams({
      scenarioId: options.scenarioId,
      appName: options.appName,
      userRequest: options.userRequest,
      pageCount: options.pageCount,
      desktopPngBytes: desktopBytes,
      mobilePngBytes: mobileBytes,
    }),
  })
  return sent ?? { giveUp: "skill-not-advertised" }
}
